'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components/atom/Skeleton/Skeleton';
import { FOCUS_RING } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import SvgWarningFill from '@common/icon/SvgWarningFill';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { CustomLink } from '@/app/_components/CustomLink';
import { EventRewardCard } from '@/app/donation/_components/EventRewardCard/EventRewardCard';
import { ShareButton } from '@/app/donation/_components/ShareButton';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { Rating } from '@/components/molecule/Rating';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import type { YearEndGoodsGift } from '@/constants/yearEndGoodsGift';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { DELIVERY_PERIOD_LABEL } from '../../../constants';
import { PriceInfo } from '../../GiftDetailHeader/_components/PriceInfo';
import { GoodsEventLabel, GoodsReveal, GoodsStockBox } from '../../YearEndGoods/YearEndGoods';
import { PrefillForm } from '../PrefillForm';
import { useSelectedDonationAmount } from '../useSelectedDonationAmount';
import { DesktopMainImages } from './DesktopMainImages';
import { MobileHero } from './MobileHero';
interface HeaderInfoSectionProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  productData?: GiftProductDetail;
  goodsGift: YearEndGoodsGift | null;
  className?: string;
}
export const HeaderInfoSection = t0 => {
  const $ = _c(84);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionName,
    productSaleStatus,
    productName,
    reviewCount,
    reviewRating,
    productNumber,
    requiredDonationAmount,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    productData,
    goodsGift,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const {
    scrollToSectionWithTab
  } = useScrollToSection();
  const t1 = productData?.currentPrice;
  let t2;
  if ($[0] !== requiredDonationAmount || $[1] !== t1) {
    t2 = {
      currentPrice: t1,
      requiredDonationAmount
    };
    $[0] = requiredDonationAmount;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    unitDonationAmount,
    totalDonationAmount
  } = useSelectedDonationAmount(t2);
  const isStoppedSale = productSaleStatus === GIFT_SALE_STATUS.STOPPED;
  const institutionRouteId = productData?.institutionSlugId ?? productData?.institutionId;
  let t3;
  if ($[3] !== onlineProductYN || $[4] !== productData?.deliveryExclusionRegionList) {
    t3 = onlineProductYN ? "" : (productData?.deliveryExclusionRegionList ?? []).map(_temp).join(", ");
    $[3] = onlineProductYN;
    $[4] = productData?.deliveryExclusionRegionList;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const deliveryExclusionRegionNames = t3;
  const t4 = reviewRating ?? 0;
  const t5 = isDesktopViewport ? 24 : 20;
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = <Rating value={t4} size={t5} />;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== reviewCount) {
    t7 = reviewCount > 0 ? <span className={cn("font-600 text-Gray-600 underline typo-body3", "mobile-tablet:typo-body4")}>{reviewCount.toLocaleString()}건 후기 보기</span> : null;
    $[9] = reviewCount;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const ratingSummary = t8;
  const imageAlt = `${institutionName} ${title} 고향사랑기부 답례품`;
  let t9;
  if ($[14] !== className) {
    t9 = cn("grid grid-cols-2 gap-28", "tablet:grid-cols-1 tablet:gap-0", "mobile:grid-cols-1 mobile:gap-0", className);
    $[14] = className;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  let t11;
  if ($[16] !== adultYN || $[17] !== heroImageUrlList || $[18] !== imageAlt) {
    t10 = <MobileHero heroImageUrlList={heroImageUrlList} adultYN={adultYN} imageAlt={imageAlt} />;
    t11 = <DesktopMainImages imageAlt={imageAlt} heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[16] = adultYN;
    $[17] = heroImageUrlList;
    $[18] = imageAlt;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("mobile-tablet:relative mobile-tablet:z-[1] mobile-tablet:-mt-20", "mobile-tablet:rounded-t-large mobile-tablet:bg-Gray-white mobile-tablet:pt-24");
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("flex flex-col px-20", "mobile-tablet:px-24");
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== institutionName || $[24] !== institutionRouteId) {
    t14 = institutionRouteId ? <CustomLink href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: institutionRouteId
    })} aria-label={`${institutionName} 상세 페이지로 이동`} className={cn("flex w-fit items-center gap-2 rounded-xsmall font-600 text-Gray-600 typo-body3", "mobile-tablet:font-400 mobile-tablet:typo-body4", "hover:underline active:opacity-80", FOCUS_RING)}>{institutionName}<SvgChevronright className="size-16 shrink-0 mobile-tablet:hidden" /></CustomLink> : <p className={cn("font-400 text-Gray-600 typo-body3", "mobile-tablet:typo-body4")}>{institutionName}</p>;
    $[23] = institutionName;
    $[24] = institutionRouteId;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ShareButton />;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t14) {
    t16 = <div className="mb-8 flex items-center justify-between gap-4">{t14}{t15}</div>;
    $[27] = t14;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = !!goodsGift;
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <GoodsEventLabel className="mb-8" />;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t17) {
    t19 = <GoodsReveal isShown={t17}>{t18}</GoodsReveal>;
    $[30] = t17;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = cn("mb-12 flex justify-between gap-16", "mobile-tablet:mb-16");
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const t21 = isStoppedSale ? "text-Gray-400" : "text-Gray-900 mobile-tablet:text-Gray-black";
  let t22;
  if ($[33] !== t21) {
    t22 = cn("flex-1 break-all font-700 typo-h3", "tablet:font-600 tablet:typo-body1", "mobile:font-600 mobile:typo-body1", t21);
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== productName || $[36] !== t22) {
    t23 = <div className={t20}><h1 className={t22}>{productName}</h1></div>;
    $[35] = productName;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = cn("mb-28 flex flex-wrap items-center gap-8", "mobile-tablet:mb-16 mobile-tablet:gap-4");
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== ratingSummary || $[40] !== reviewCount || $[41] !== scrollToSectionWithTab) {
    t25 = <div className={t24}>{reviewCount > 0 ? <button type="button" onClick={() => scrollToSectionWithTab("review")} className={cn("flex items-center gap-8 rounded-xsmall transition-opacity duration-200", "mobile-tablet:gap-4", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500")}>{ratingSummary}</button> : <div className={cn("flex items-center gap-8", "mobile-tablet:gap-4")}>{ratingSummary}</div>}</div>;
    $[39] = ratingSummary;
    $[40] = reviewCount;
    $[41] = scrollToSectionWithTab;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== unitDonationAmount) {
    t26 = <div className="mb-20"><PriceInfo amount={unitDonationAmount} /></div>;
    $[43] = unitDonationAmount;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== t16 || $[46] !== t19 || $[47] !== t23 || $[48] !== t25 || $[49] !== t26) {
    t27 = <div className={t13}>{t16}{t19}{t23}{t25}{t26}</div>;
    $[45] = t16;
    $[46] = t19;
    $[47] = t23;
    $[48] = t25;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = cn("mx-20 flex flex-col gap-24 border-t border-Gray-200 pb-16 pt-20", "mobile-tablet:mx-0 mobile-tablet:gap-16 mobile-tablet:p-20");
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== onlineProductDesc || $[53] !== onlineProductYN) {
    t29 = onlineProductYN ? <div className={cn("flex flex-col gap-16 font-500 typo-body3", "mobile-tablet:gap-12 mobile-tablet:font-400 mobile-tablet:typo-body4")}><div className="flex items-start gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">배송 정보</p><p className="text-Gray-550 mobile-tablet:text-Gray-900">온라인 답례품</p></div>{onlineProductDesc ? <div className="flex items-start gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">답례품 설명</p><p className="whitespace-pre-line text-Gray-550 mobile-tablet:text-Gray-900">{onlineProductDesc}</p></div> : null}</div> : <div className={cn("flex flex-col gap-16 font-500 typo-body3", "mobile-tablet:gap-12 mobile-tablet:font-400 mobile-tablet:typo-body4")}><div className="flex items-center gap-56 mobile-tablet:gap-16"><p className="w-92 text-Gray-600">배송 정보</p><p className="text-Gray-550 mobile-tablet:text-Gray-900">무료배송</p></div><div className="flex gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">배송 안내</p><div className={cn("flex flex-wrap gap-x-4 text-Gray-550 mobile-tablet:text-Gray-900", "mobile-tablet:flex-col mobile-tablet:gap-4")}><p>{DELIVERY_PERIOD_LABEL}</p><p className="mobile-tablet:font-500">(식품의 경우 지연 발생 시 1주~2주 소요)</p></div></div></div>;
    $[52] = onlineProductDesc;
    $[53] = onlineProductYN;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== deliveryExclusionRegionNames || $[56] !== exchangeYN) {
    t30 = !exchangeYN || deliveryExclusionRegionNames ? <ul className="flex flex-col gap-8 rounded-xsmall border border-Gray-300 px-20 py-12 font-400 text-Gray-600 typo-caption1">{!exchangeYN ? <li className="flex items-center gap-4"><SvgWarningFill className="size-13 shrink-0 text-B-500" /><p>해당 답례품은 반품/교환이 불가해요.</p></li> : null}{deliveryExclusionRegionNames ? <li className="flex items-start gap-4"><SvgWarningFill className="mt-3 size-13 shrink-0 text-B-500" /><p className="shrink-0">배송불가 지역</p><span aria-hidden={true} className="text-Gray-500 typo-caption2">|</span><p className="text-Gray-900">{deliveryExclusionRegionNames}</p></li> : null}</ul> : null;
    $[55] = deliveryExclusionRegionNames;
    $[56] = exchangeYN;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] !== t29 || $[59] !== t30) {
    t31 = <div className={t28}>{t29}{t30}</div>;
    $[58] = t29;
    $[59] = t30;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== productData || $[62] !== productNumber) {
    t32 = <PrefillForm className="px-20" productData={productData} productNumber={productNumber} />;
    $[61] = productData;
    $[62] = productNumber;
    $[63] = t32;
  } else {
    t32 = $[63];
  }
  let t33;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Skeleton className="h-56 w-full rounded-medium" />;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] !== productNumber || $[66] !== totalDonationAmount) {
    t34 = <div className="px-20 pt-16"><Suspense fallback={t33}><EventRewardCard productNumber={productNumber} donationAmount={totalDonationAmount} defaultExpanded={true} /></Suspense></div>;
    $[65] = productNumber;
    $[66] = totalDonationAmount;
    $[67] = t34;
  } else {
    t34 = $[67];
  }
  const t35 = !!goodsGift;
  let t36;
  if ($[68] !== goodsGift) {
    t36 = goodsGift ? <GoodsStockBox goodsGift={goodsGift} className="mx-20 mt-12 mobile-tablet:mt-16" /> : null;
    $[68] = goodsGift;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] !== t35 || $[71] !== t36) {
    t37 = <GoodsReveal isShown={t35}>{t36}</GoodsReveal>;
    $[70] = t35;
    $[71] = t36;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  let t38;
  if ($[73] !== t27 || $[74] !== t31 || $[75] !== t32 || $[76] !== t34 || $[77] !== t37) {
    t38 = <div className={t12}><section className="flex w-full flex-col">{t27}{t31}{t32}{t34}{t37}</section></div>;
    $[73] = t27;
    $[74] = t31;
    $[75] = t32;
    $[76] = t34;
    $[77] = t37;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== t10 || $[80] !== t11 || $[81] !== t38 || $[82] !== t9) {
    t39 = <div className={t9}>{t10}{t11}{t38}</div>;
    $[79] = t10;
    $[80] = t11;
    $[81] = t38;
    $[82] = t9;
    $[83] = t39;
  } else {
    t39 = $[83];
  }
  return t39;
};
function _temp(region) {
  return region.regionName;
}